import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Popover,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import Iconify from '../../../components/Iconify';
import useAutomationProgress from '../../../hooks/useAutomationProgress';
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';

// The top-bar view of automations you just set off.
//
// It exists because the work is asynchronous but the intent was not: you added
// a subscription and then the page went quiet while a celery worker talked to a
// captive portal somewhere. This says out loud what is happening and turns green
// when it lands.
//
// Every word it shows comes from the backend registry, so a future action (door
// unlock, Spintly) appears here with no change to this file.

// The pill is deliberately the one showy thing in the header. Everything else up
// there is a control you go looking for; this appears on its own and has about
// four seconds to say "something clever is happening for you". The colours come
// from the theme's primary and secondary, so it is the operator's own brand
// doing the sparkling, not ours.
const spin = keyframes`
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const shimmer = keyframes`
  0%   { transform: translateX(-120%); }
  60%  { transform: translateX(320%); }
  100% { transform: translateX(320%); }
`;

const rotate = keyframes`
  to { transform: rotate(360deg); }
`;

// The landing. A little overshoot is the whole difference between "the state
// changed" and "it worked".
const pop = keyframes`
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.25); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const settle = keyframes`
  0%   { box-shadow: 0 0 0 0 var(--glow); }
  100% { box-shadow: 0 0 0 12px transparent; }
`;

const STATE_META = {
  queued: { color: 'text.secondary', icon: 'eva:clock-outline' },
  running: { color: 'info.main', icon: null },
  success: { color: 'success.main', icon: 'eva:checkmark-circle-2-fill' },
  failed: { color: 'error.main', icon: 'eva:alert-circle-fill' },
  skipped: { color: 'text.disabled', icon: 'eva:slash-outline' },
};

// Reasons a run can end without doing anything, phrased for an operator rather
// than for a log file.
const SKIP_REASONS = {
  no_active_subscription: 'no active subscription',
  no_mobile: 'no mobile number on file',
  member_inactive: 'member is not active',
  no_company: 'not attached to a company',
  not_reversible: 'nothing to undo',
  no_actions: 'the automation has no actions',
  rule_inactive_or_missing: 'the automation was switched off',
  subject_missing: 'the member could not be found',
  already_sent: 'already sent once',
  not_configured: 'not set up yet',
};

function StateIcon({ state, size = 18 }) {
  const meta = STATE_META[state] || STATE_META.queued;
  if (state === 'running') {
    return <CircularProgress size={size - 2} thickness={5} sx={{ color: 'info.main' }} />;
  }
  return <Iconify icon={meta.icon} sx={{ width: size, height: size, color: meta.color }} />;
}

// The pill's own icon. The list inside the popover keeps the plain spinner,
// which is the right amount of drama for a row in a list.
function PillIcon({ busy, state }) {
  if (busy) {
    // A processing icon, plainly. The ring around the pill carries the flourish;
    // this part only has to say "still working".
    return (
      <Iconify
        icon="eva:sync-outline"
        sx={{
          width: 19,
          height: 19,
          flexShrink: 0,
          color: 'primary.main',
          animation: `${rotate} 1.4s linear infinite`,
        }}
      />
    );
  }

  const meta = STATE_META[state] || STATE_META.success;
  return (
    <Iconify
      icon={meta.icon || STATE_META.success.icon}
      sx={{
        width: 20,
        height: 20,
        flexShrink: 0,
        color: meta.color,
        animation: `${pop} .45s cubic-bezier(.34,1.56,.64,1)`,
      }}
    />
  );
}

function currentLine(run) {
  // What the pill says: the step that is moving right now, or the last thing
  // that happened once everything has stopped.
  const running = run.steps.find((s) => s.state === 'running');
  if (running && running.label) return running.label;

  const pending = run.steps.find((s) => s.state === 'queued');
  if (run.state === 'queued' && pending && pending.label) return pending.label;

  const finished = [...run.steps].reverse().find((s) => s.label);
  if (finished && finished.label) return finished.label;

  return run.ruleName;
}

function reasonText(reason) {
  if (!reason) return '';
  return SKIP_REASONS[reason] || reason.replace(/_/g, ' ');
}

function RunRow({ run, onDismiss }) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <StateIcon state={run.state} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {run.ruleName}
          </Typography>
          {run.subject && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {run.subject}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => onDismiss(run.runId)}>
          <Iconify icon="eva:close-fill" sx={{ width: 16, height: 16 }} />
        </IconButton>
      </Stack>

      <Stack spacing={0.75} sx={{ mt: 1, pl: 0.5 }}>
        {run.steps.map((step) => {
          const meta = STATE_META[step.state] || STATE_META.queued;
          return (
            <Stack key={step.actionSlug} direction="row" alignItems="center" spacing={1}>
              <StateIcon state={step.state} size={16} />
              <Typography variant="body2" sx={{ color: meta.color, flexGrow: 1, minWidth: 0 }} noWrap>
                {step.label || step.name || step.actionSlug}
              </Typography>
              {step.state === 'skipped' && step.reason && (
                <Typography variant="caption" color="text.disabled" noWrap>
                  {reasonText(step.reason)}
                </Typography>
              )}
            </Stack>
          );
        })}

        {!run.steps.length && run.reason && (
          <Typography variant="body2" color="text.disabled">
            Skipped: {reasonText(run.reason)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function AutomationProgress() {
  const { user } = useAuth();
  const isDesktop = useResponsive('up', 'sm');
  // A ref for the anchor and a boolean for the open state, rather than holding
  // the DOM node in state. Holding the node meant that when the last run was
  // dismissed the pill unmounted while the node stayed in state, and the next
  // automation re-opened the popover on its own against an element that was no
  // longer on the page, which Popover positions at the top-left corner.
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Members never wire up automations, so they have nothing to watch.
  const { runs, activeCount, dismiss, dismissAll } = useAutomationProgress({ enabled: user?.role === 'org' });

  useEffect(() => {
    if (!runs.length) setOpen(false);
  }, [runs.length]);

  if (!runs.length) return null;

  const busy = activeCount > 0;
  const latest = runs[0];
  const anyFailed = runs.some((r) => r.state === 'failed');
  const tone = busy ? 'info' : anyFailed ? 'error' : 'success';

  return (
    <>
      <Tooltip title={busy ? 'An automation is running' : 'Automation finished'}>
        {/* Two boxes, not one: the outer is the rotating gradient, the inner
            covers all but a hairline of it. That hairline is the ring. A plain
            border cannot rotate. */}
        <Box
          ref={anchorRef}
          onClick={() => setOpen(true)}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: '1.5px',
            mr: 1,
            cursor: 'pointer',
            borderRadius: '999px',
            maxWidth: { xs: 46, sm: 268, lg: 348 },
            isolation: 'isolate',
            '--glow': (theme) => alpha(theme.palette[tone].main, 0.45),
            animation: busy ? 'none' : `${settle} .9s ease-out`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '260%',
              aspectRatio: '1',
              transform: 'translate(-50%, -50%)',
              background: (theme) =>
                busy
                  ? `conic-gradient(${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${alpha(theme.palette.primary.main, 0.05)}, ${theme.palette.primary.main})`
                  : alpha(theme.palette[tone].main, 0.34),
              animation: busy ? `${spin} 2.4s linear infinite` : 'none',
            },
            '&:hover': { filter: 'brightness(1.04)' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 33,
              px: 1.25,
              borderRadius: 'inherit',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              backgroundImage: (theme) =>
                busy
                  ? `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`
                  : `linear-gradient(120deg, ${alpha(theme.palette[tone].main, 0.1)}, ${alpha(theme.palette[tone].main, 0.04)})`,
              color: busy ? 'text.primary' : `${tone}.dark`,
              transition: (theme) => theme.transitions.create(['color', 'background-image']),
              // The light passing over it. Purely decorative, and the only part
              // of this that is there to be admired.
              '&::after': busy
                ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '38%',
                    background: (theme) =>
                      `linear-gradient(100deg, transparent, ${alpha(theme.palette.common.white, 0.55)}, transparent)`,
                    animation: `${shimmer} 2.6s ease-in-out infinite`,
                    pointerEvents: 'none',
                  }
                : undefined,
            }}
          >
            <PillIcon busy={busy} state={latest.state} />

            {isDesktop && (
              <Typography
                variant="caption"
                noWrap
                sx={{ fontWeight: 600, letterSpacing: 0.1, position: 'relative' }}
              >
                {currentLine(latest)}
                {runs.length > 1 ? `  +${runs.length - 1}` : ''}
              </Typography>
            )}
          </Box>
        </Box>
      </Tooltip>

      <Popover
        open={open && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '92vw', mt: 1 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Automations
          </Typography>
          <Typography
            variant="caption"
            onClick={() => {
              dismissAll();
              setOpen(false);
            }}
            sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            Clear
          </Typography>
        </Stack>

        <Divider />

        {runs.map((run, index) => (
          <Box key={run.runId}>
            {index > 0 && <Divider />}
            <RunRow run={run} onDismiss={dismiss} />
          </Box>
        ))}

        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.25 }}>
          Only you see this. Everything that ran is kept in the automation activity log.
        </Typography>
      </Popover>
    </>
  );
}
